<template>
  <n-modal-background
    v-if="open"
    :suppressed="suppressedForUnlock"
    :closeOnEscape="!suppressedForUnlock"
    :closeOnOutside="!suppressedForUnlock"
    @close="handleClose"
    ref="bg"
  >
    <n-simple-modal-body :title="modalTitle" :titleSuffix="'[' + $t('general.beta') + ']'" @close="$refs.bg.close()">
      <div class="form-fields">
        <n-input :value="effectiveVariableName" :label="$t('variableModal.variableLabel')" disabled />
        <n-input
          :value="displayLabel"
          :label="$t('variableModal.displayLabelLabel')"
          :placeholder="$t('variableModal.displayLabelPlaceholder')"
          :maxlength="maxDisplayLabelLength"
          @input="onDisplayLabelInput"
        />
        <n-select
          :value="selectedType"
          :options="typeOptions"
          :label="$t('variableModal.typeLabel')"
          :error="typeError"
          :toLabel="toLabel"
          @select="onTypeSelect"
        />
        <n-select
          v-if="isDateLikeType"
          :value="selectedDateFormat"
          :options="dateFormatOptions"
          :label="dateFormatLabel"
          :error="dateFormatError"
          :toLabel="formatOptionLabel"
          @select="onDateFormatSelect"
        />
        <p v-if="isDateLikeType && dateFormatError" class="field-error">{{ dateFormatError }}</p>
        <div v-if="isDateLikeType && selectedType" class="date-picker-field">
          <label class="date-picker-label">{{ $t('variableModal.defaultValueLabel') }}</label>
          <date-picker
            v-model="defaultValue"
            :placeholder="datePlaceholder"
            :format="datePickerFormat"
            :type="datePickerType"
            prefix-class="variable-date"
            valueType="format"
            @input="onDefaultValueInput"
          />
          <p v-if="defaultValueError" class="field-error">{{ defaultValueError }}</p>
        </div>
        <div v-if="isDropdownType && selectedType" class="dropdown-options-field">
          <label class="dropdown-options-label">
            {{ $t('variableModal.dropdownOptionsLabel') || 'Dropdown Options' }}
          </label>
          <div
            ref="dropdownOptionsScrollContainer"
            class="dropdown-options-scroll-container"
            :class="{ 'show-scroll': shouldShowScroll }"
          >
            <div class="dropdown-options-list">
              <div v-for="(option, index) in dropdownOptions" :key="index" class="dropdown-option-row">
                <n-input
                  :value="option"
                  :placeholder="$t('variableModal.dropdownOptionPlaceholder') || 'Enter option value'"
                  :error="optionErrors[index]"
                  :maxlength="maxOptionLength"
                  @input="onDropdownOptionInput(index, $event)"
                  @keydown="onDropdownOptionKeyDown(index, $event)"
                  @blur="onDropdownOptionBlur(index)"
                  class="dropdown-option-input"
                />
                <div class="dropdown-option-actions">
                  <n-icon icon="trash" size="s" class="delete-icon" @click="removeDropdownOption(index)" />
                  <n-icon
                    v-if="index === dropdownOptions.length - 1 && dropdownOptions.length < maxDropdownOptions"
                    icon="plus"
                    size="s"
                    class="add-icon"
                    @click="addDropdownOption"
                  />
                </div>
              </div>
            </div>
          </div>
          <p v-if="dropdownOptionsError" class="field-error">{{ dropdownOptionsError }}</p>
        </div>
        <n-select
          v-if="isDropdownType && selectedType"
          :value="defaultValue || undefined"
          :options="dropdownDefaultValueOptions"
          :label="$t('variableModal.defaultValueLabel')"
          :placeholder="$t('variableModal.defaultValueDropdownPlaceholder') || 'Select a default value'"
          :error="defaultValueError"
          :toLabel="toLabelDropdownDefault"
          @select="onDefaultValueSelect"
        />
        <n-input
          v-else-if="selectedType && !isDateLikeType && !isDropdownType"
          :value="defaultValue"
          :autofocus="!isEditMode"
          :label="$t('variableModal.defaultValueLabel')"
          :placeholder="$t('variableModal.defaultValuePlaceholder')"
          :error="defaultValueError"
          :type="getInputType"
          :maxlength="maxDefaultValueLength"
          @input="onDefaultValueInput"
          @keydown="onKeyDown"
          @blur="onDefaultValueBlur"
        />
      </div>
      <template #rightFooter>
        <n-button :label="submitButtonLabel" @click="handleSubmit" :disabled="!isValid || loading" :loading="loading" />
      </template>
    </n-simple-modal-body>
  </n-modal-background>
</template>

<script lang="ts">
import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator'
import VueConstructor from 'vue'
import { mapStores } from 'pinia'
import { VariableType } from '@/modules/global/stores/VariableTypeStore'
import { useSpaceVariableStore, SpaceVariableStore } from '@/modules/space/stores/SpaceVariableStore'
import { useSnackbarStore, SnackbarStore } from '@/modules/global/stores/SnackbarStore'
import { useSpaceStore } from '@/modules/space/stores/SpaceStore'
import { getCurrentSpaceId } from '@/router/helpers'
import SpaceVariable from '@/modules/space/models/SpaceVariable'
import CreateSpaceVariableRequest from '@/modules/space/models/CreateSpaceVariableRequest'
import { sanitizeDisplayLabel } from '@/util/string'
import datePicker from 'vue2-datepicker'
import { isValid, parse } from 'date-fns'
import '@/assets/styles/variable-date-picker.scss'
import {
  buildDateFormatOptions,
  buildDateTimeFormatOptions,
  DateFormatOption,
  getDefaultDateFormat,
  getDefaultDateTimeFormat,
  toDatePickerFormat
} from '@/util/date-format-options'
import { mapTypeToApi, mapApiTypeToUI } from '@/util/variable-type-utils'
import { validatePhone } from '@/util/phone-validation'
import {
  cancelVariableSchemaUnlockFlow,
  handleDocumentSignedVariableSchemaError,
  promptUnlockIfVariableSchemaLocked
} from '@/util/variable-schema-lock'

// Constants - compiled once for better performance
const TYPE_OPTIONS: VariableType[] = ['Text', 'Number', 'E-mail', 'Date', /* 'DateTime', */ 'Phone', 'Dropdown']
const DROPDOWN_OPTION_REGEX = /^[A-Za-z0-9 _-]*$/
const DROPDOWN_OPTION_INVALID_CHARS_REGEX = /[^A-Za-z0-9 _-]/g
const DROPDOWN_OPTION_LEADING_SPACE_REGEX = /^\s+/
const MAX_DROPDOWN_OPTIONS = 50
const MIN_DROPDOWN_OPTIONS = 2
const MAX_OPTION_LENGTH = 50
const MIN_OPTION_LENGTH = 1
const MAX_DISPLAY_LABEL_LENGTH = 50
const MAX_DEFAULT_VALUE_LENGTH = 50

// Error messages
const ERROR_EMPTY_OPTIONS = 'All options must have a value. Please fill in or remove empty options.'
const ERROR_MIN_OPTIONS = `At least ${MIN_DROPDOWN_OPTIONS} options are required`
const ERROR_OPTION_LENGTH = `Option must be between ${MIN_OPTION_LENGTH} and ${MAX_OPTION_LENGTH} characters`
const ERROR_INVALID_CHARACTERS =
  'Invalid characters. Only letters, numbers, spaces, hyphens, and underscores are allowed'
const ERROR_DUPLICATE_OPTIONS = 'All options must be unique'
const ERROR_DUPLICATE_OPTION = 'This option already exists'
const NUMBER_ALLOWED_KEYS = new Set([
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '.',
  'Backspace',
  'Delete',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Tab',
  'Enter'
])
const NON_NUMERIC_REGEX = /[^0-9.]/g
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_ALLOWED_KEYS = new Set([
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  'Backspace',
  'Delete',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Tab',
  'Enter'
])
const NON_DIGIT_REGEX = /[^0-9]/g

@Component({
  components: { datePicker },
  computed: {
    ...mapStores(useSpaceVariableStore, useSnackbarStore)
  }
})
export default class VariableModal extends Vue {
  @Prop() variable?: SpaceVariable | null
  @Prop({ required: true }) open!: boolean
  @Prop({ default: null }) scopeId!: string | null
  @Prop() variableName?: string

  @Ref('bg') bgRef!: any
  @Ref('dropdownOptionsScrollContainer') dropdownOptionsScrollContainer!: HTMLElement

  spaceVariableStore!: SpaceVariableStore
  snackbarStore!: SnackbarStore

  variableNameData = ''
  displayLabel = ''
  selectedType: VariableType = 'Text'
  defaultValue = ''
  selectedDateFormat: DateFormatOption | null = null
  dropdownOptions: string[] = []
  optionErrors: string[] = []
  dropdownOptionsError = ''
  variableNameError = ''
  typeError = ''
  defaultValueError = ''
  dateFormatError = ''
  loading = false
  suppressedForUnlock = false

  private variableSchemaUnlockOptions() {
    return {
      resume: () => {
        this.suppressedForUnlock = false
      },
      onCancel: () => {
        this.suppressedForUnlock = false
      }
    }
  }

  get isEditMode(): boolean {
    return !!this.variable
  }

  get effectiveVariableName(): string {
    return this.variable?.variableName ?? this.variableName ?? this.variableNameData ?? ''
  }

  get modalTitle(): string {
    return this.isEditMode
      ? (this.$t('variableModal.titleModify') as string)
      : (this.$t('variableModal.titleCreate') as string)
  }

  get submitButtonLabel(): string {
    return this.isEditMode ? (this.$t('variableModal.save') as string) : (this.$t('variableModal.submit') as string)
  }

  @Watch('open', { immediate: true })
  watchOpen(newVal: boolean) {
    if (!newVal && this.suppressedForUnlock) {
      cancelVariableSchemaUnlockFlow()
    }
    this.suppressedForUnlock = false
    if (newVal) {
      this.initializeForm()
    }
  }

  @Watch('variable')
  watchVariable() {
    if (this.open) {
      this.initializeForm()
    }
  }

  @Watch('variableName')
  watchVariableName(newVal: string, oldVal: string) {
    if (!this.open || this.isEditMode) return
    if (!this.displayLabel.trim() || this.displayLabel === oldVal) {
      this.displayLabel = (newVal || '').substring(0, MAX_DISPLAY_LABEL_LENGTH)
    }
  }

  mounted() {
    if (this.open) {
      this.$nextTick(() => {
        this.initializeForm()
      })
    }
  }

  initializeForm() {
    if (this.isEditMode && this.variable) {
      this.$set(this, 'variableNameData', this.variable.variableName || '')
      const displayLabel = this.variable.displayLabel || this.variable.variableName || ''
      this.$set(this, 'displayLabel', displayLabel.substring(0, MAX_DISPLAY_LABEL_LENGTH))
      const mappedType = mapApiTypeToUI(this.variable.variableType || '')
      this.$set(this, 'selectedType', mappedType)
      const defaultValue = this.variable.defaultValue || ''
      this.$set(this, 'defaultValue', defaultValue.substring(0, MAX_DEFAULT_VALUE_LENGTH))
      if (mappedType === 'Date' || mappedType === 'DateTime') {
        const options = this.dateFormatOptions
        const selected = options.find(option => option.value === this.variable!.dateFormat)
        this.$set(this, 'selectedDateFormat', selected || (options[0] ?? null))
      } else {
        this.$set(this, 'selectedDateFormat', null)
      }
      if (mappedType === 'Dropdown' && this.variable.options) {
        const sortedOptions = [...this.variable.options].sort((a, b) => a.order - b.order)
        this.$set(
          this,
          'dropdownOptions',
          sortedOptions.map(opt => opt.value.trim())
        )
        this.$set(this, 'optionErrors', new Array(sortedOptions.length).fill(''))
      } else {
        this.$set(this, 'dropdownOptions', [])
        this.$set(this, 'optionErrors', [])
      }
    } else {
      const name = this.variableName || ''
      this.$set(this, 'variableNameData', name)
      this.$set(this, 'displayLabel', name.substring(0, MAX_DISPLAY_LABEL_LENGTH))
      this.$set(this, 'selectedType', 'Text')
      this.$set(this, 'defaultValue', '')
      this.$set(this, 'selectedDateFormat', null)
      this.$set(this, 'dropdownOptions', ['', ''])
      this.$set(this, 'optionErrors', ['', ''])
    }
    this.$set(this, 'variableNameError', '')
    this.$set(this, 'typeError', '')
    this.$set(this, 'defaultValueError', '')
    this.$set(this, 'dateFormatError', '')
    this.$set(this, 'dropdownOptionsError', '')
  }

  get typeOptions(): VariableType[] {
    return TYPE_OPTIONS
  }

  get maxDropdownOptions(): number {
    return MAX_DROPDOWN_OPTIONS
  }

  get maxOptionLength(): number {
    return MAX_OPTION_LENGTH
  }

  get maxDisplayLabelLength(): number {
    return MAX_DISPLAY_LABEL_LENGTH
  }

  get maxDefaultValueLength(): number {
    return MAX_DEFAULT_VALUE_LENGTH
  }

  get minDropdownOptions(): number {
    return MIN_DROPDOWN_OPTIONS
  }

  get isDateLikeType(): boolean {
    return this.selectedType === 'Date' || this.selectedType === 'DateTime'
  }

  get isDropdownType(): boolean {
    return this.selectedType === 'Dropdown'
  }

  get dropdownOptionsForSelect(): string[] {
    return this.getTrimmedDropdownOptions()
  }

  get dropdownDefaultValueOptions(): string[] {
    return ['', ...this.dropdownOptionsForSelect]
  }

  clearDefaultValueIfInvalidForDropdown() {
    if (!this.isDropdownType) return
    const currentDefault = (this.defaultValue || '').trim()
    if (currentDefault && !this.dropdownOptionsForSelect.includes(currentDefault)) {
      this.defaultValue = ''
      this.defaultValueError = ''
    }
  }

  get shouldShowScroll(): boolean {
    return this.dropdownOptions.length > 4
  }

  get isDateTimeType(): boolean {
    return this.selectedType === 'DateTime'
  }

  get dateFormatLabel(): string {
    return this.isDateTimeType
      ? (this.$t('variableModal.datetimeFormatLabel') as string)
      : (this.$t('variableModal.dateFormatLabel') as string)
  }

  get dateFormatOptions(): DateFormatOption[] {
    return this.isDateTimeType ? buildDateTimeFormatOptions() : buildDateFormatOptions()
  }

  get selectedDateFormatValue(): string {
    if (this.selectedDateFormat?.value) return this.selectedDateFormat.value
    return this.isDateTimeType ? getDefaultDateTimeFormat() : getDefaultDateFormat()
  }

  get datePickerFormat(): string {
    return toDatePickerFormat(this.selectedDateFormatValue)
  }

  get datePickerType(): string {
    return this.isDateTimeType ? 'datetime' : 'date'
  }

  get datePlaceholder(): string {
    if (!this.isDateLikeType) return ''
    return this.selectedDateFormat?.formatLabel || ''
  }

  get isValid(): boolean {
    const nameToCheck = this.effectiveVariableName
    if (!nameToCheck?.trim()) return false
    if (!this.selectedType) return false
    if (this.isDateLikeType && !this.selectedDateFormat) return false
    if (this.isDropdownType && !this.validateDropdownOptions()) return false

    const defaultValue = this.defaultValue || ''
    const trimmedValue = defaultValue.trim()
    if (!trimmedValue) return true

    switch (this.selectedType) {
      case 'Number':
        return !isNaN(Number(trimmedValue)) && trimmedValue !== ''
      case 'E-mail':
        return EMAIL_REGEX.test(trimmedValue)
      case 'Phone':
        return validatePhone(trimmedValue).isValid
      case 'Date':
      case 'DateTime':
        return this.isValidDateValue(trimmedValue, this.selectedDateFormatValue)
      case 'Dropdown':
        return this.dropdownOptionsForSelect.includes(trimmedValue)
      default:
        return true
    }
  }

  get getInputType(): string {
    return this.selectedType === 'E-mail' ? 'email' : 'text'
  }

  toLabel(opt: VariableType): string {
    return opt
  }

  toLabelOption(opt: string): string {
    return opt
  }

  toLabelDropdownDefault(opt: string): string {
    return opt === '' ? (this.$t('variableModal.noDefault') as string) : opt
  }

  formatOptionLabel(option: DateFormatOption): string {
    return option.label
  }

  onTypeSelect(type: VariableType) {
    this.selectedType = type
    this.typeError = ''
    this.defaultValue = ''
    this.defaultValueError = ''
    this.dateFormatError = ''
    this.dropdownOptionsError = ''
    if (type === 'Date' || type === 'DateTime') {
      const options = this.dateFormatOptions
      this.selectedDateFormat = options.length > 0 ? options[0] : null
    } else {
      this.selectedDateFormat = null
    }
    if (type === 'Dropdown') {
      if (!this.dropdownOptions || this.dropdownOptions.length === 0) {
        this.$set(this, 'dropdownOptions', ['', ''])
        this.$set(this, 'optionErrors', ['', ''])
      }
    } else {
      this.$set(this, 'dropdownOptions', [])
      this.$set(this, 'optionErrors', [])
    }
  }

  onDateFormatSelect(option: DateFormatOption) {
    this.selectedDateFormat = option
    this.dateFormatError = ''
    this.defaultValue = ''
    this.defaultValueError = ''
  }

  onDisplayLabelInput(value: string) {
    this.displayLabel = (value || '').substring(0, MAX_DISPLAY_LABEL_LENGTH)
  }

  onDefaultValueInput(value: string | null | undefined) {
    const type = this.selectedType
    let filtered = value ?? ''

    if (type === 'Number') {
      filtered = filtered.replace(NON_NUMERIC_REGEX, '')
      const dotIndex = filtered.indexOf('.')
      if (dotIndex !== -1) {
        filtered = filtered.substring(0, dotIndex + 1) + filtered.substring(dotIndex + 1).replace(/\./g, '')
      }
    } else if (type === 'Phone') {
      filtered = filtered.replace(NON_DIGIT_REGEX, '')
    }

    filtered = filtered.substring(0, MAX_DEFAULT_VALUE_LENGTH)
    this.defaultValue = filtered

    const trimmed = filtered.trim()
    if (trimmed) {
      this.validateDefaultValue()
    } else {
      this.defaultValueError = ''
    }
  }

  onDefaultValueBlur() {
    if (this.defaultValue?.trim()) {
      this.validateDefaultValue()
    }
  }

  onKeyDown(event: KeyboardEvent) {
    const type = this.selectedType
    const isControlKey = event.ctrlKey || event.altKey || event.metaKey

    if (type === 'Number') {
      if (!isControlKey && !NUMBER_ALLOWED_KEYS.has(event.key)) {
        event.preventDefault()
        return false
      }
      if (event.key === '.' && this.defaultValue?.includes('.')) {
        event.preventDefault()
        return false
      }
    } else if (type === 'Phone') {
      if (!isControlKey && !PHONE_ALLOWED_KEYS.has(event.key)) {
        event.preventDefault()
        return false
      }
    }
    return true
  }

  validateDefaultValue(): boolean {
    const defaultValue = this.defaultValue || ''
    const trimmedValue = defaultValue.trim()
    if (!trimmedValue) {
      this.defaultValueError = ''
      return true
    }

    switch (this.selectedType) {
      case 'Date':
      case 'DateTime': {
        if (!this.selectedDateFormat) {
          this.dateFormatError = this.$t('variableModal.errors.dateFormatRequired') as string
          return false
        }
        if (!this.isValidDateValue(trimmedValue, this.selectedDateFormat.value)) {
          this.defaultValueError = this.$t('variableModal.errors.invalidDate') as string
          return false
        }
        break
      }
      case 'Number': {
        const numValue = Number(trimmedValue)
        if (isNaN(numValue) || trimmedValue === '') {
          this.defaultValueError = this.$t('variableModal.errors.invalidNumber') as string
          return false
        }
        break
      }
      case 'E-mail': {
        if (!EMAIL_REGEX.test(trimmedValue)) {
          this.defaultValueError = this.$t('variableModal.errors.invalidEmail') as string
          return false
        }
        break
      }
      case 'Phone': {
        const validation = validatePhone(trimmedValue)
        if (!validation.isValid) {
          this.defaultValueError = validation.error || (this.$t('variableModal.errors.invalidPhone') as string)
          return false
        }
        break
      }
    }

    this.defaultValueError = ''
    return true
  }

  isValidDateValue(value: string, formatValue: string): boolean {
    const parsed = parse(value, formatValue, new Date())
    return isValid(parsed)
  }

  addDropdownOption() {
    if (this.dropdownOptions.length < MAX_DROPDOWN_OPTIONS) {
      this.dropdownOptions.push('')
      this.optionErrors.push('')

      this.$nextTick(() => {
        const scrollContainer =
          this.dropdownOptionsScrollContainer || (this.$refs.dropdownOptionsScrollContainer as HTMLElement)
        if (scrollContainer) {
          requestAnimationFrame(() => {
            scrollContainer.scrollTop = scrollContainer.scrollHeight
          })
        }
      })
    }
  }

  removeDropdownOption(index: number) {
    if (this.dropdownOptions.length > MIN_DROPDOWN_OPTIONS) {
      this.dropdownOptions.splice(index, 1)
      this.optionErrors.splice(index, 1)
      this.validateAllDropdownOptions()
      this.clearDefaultValueIfInvalidForDropdown()
    } else {
      this.$set(this.dropdownOptions, index, '')
      this.$set(this.optionErrors, index, '')
      this.clearDefaultValueIfInvalidForDropdown()
    }
  }

  getTrimmedDropdownOptions(): string[] {
    return this.dropdownOptions.map(opt => opt.trim()).filter(Boolean)
  }

  sanitizeDropdownOptionValue(value: string): string {
    return value
      .replace(DROPDOWN_OPTION_INVALID_CHARS_REGEX, '')
      .replace(DROPDOWN_OPTION_LEADING_SPACE_REGEX, '')
      .substring(0, MAX_OPTION_LENGTH)
  }

  normalizeDropdownOptionAtIndex(index: number): void {
    const value = this.dropdownOptions[index] || ''
    const trimmed = value.trim()
    if (trimmed === value) return

    if ((this.defaultValue || '') === value) {
      this.defaultValue = trimmed
    }
    this.$set(this.dropdownOptions, index, trimmed)
  }

  onDropdownOptionInput(index: number, value: string) {
    const sanitized = this.sanitizeDropdownOptionValue(value)
    if (sanitized === this.dropdownOptions[index]) return

    this.$set(this.dropdownOptions, index, sanitized)
    this.$set(this.optionErrors, index, '')
    this.validateDropdownOption(index)
    this.validateUniqueness()
    this.clearDefaultValueIfInvalidForDropdown()
  }

  onDropdownOptionKeyDown(index: number, event: KeyboardEvent) {
    const isControlKey = event.ctrlKey || event.altKey || event.metaKey
    if (isControlKey) return true

    const key = event.key
    if (key === ' ' || key === 'Spacebar') {
      const input = event.target as HTMLInputElement | null
      const selectionStart = input?.selectionStart ?? 0
      if (selectionStart === 0) {
        event.preventDefault()
        return false
      }
    }

    if (key.length === 1 && !DROPDOWN_OPTION_REGEX.test(key)) {
      event.preventDefault()
      return false
    }
    return true
  }

  onDropdownOptionBlur(index: number) {
    this.normalizeDropdownOptionAtIndex(index)
    this.validateAllDropdownOptions()
    this.clearDefaultValueIfInvalidForDropdown()
  }

  validateDropdownOption(index: number): boolean {
    const option = this.dropdownOptions[index] || ''
    const trimmed = option.trim()

    if (!trimmed) {
      this.$set(this.optionErrors, index, '')
      return true
    }

    if (trimmed.length < MIN_OPTION_LENGTH || trimmed.length > MAX_OPTION_LENGTH) {
      this.$set(this.optionErrors, index, ERROR_OPTION_LENGTH)
      return false
    }

    if (!DROPDOWN_OPTION_REGEX.test(trimmed)) {
      this.$set(this.optionErrors, index, ERROR_INVALID_CHARACTERS)
      return false
    }

    this.$set(this.optionErrors, index, '')
    return true
  }

  validateUniqueness(): void {
    this.optionErrors.forEach((error, index) => {
      if (error === ERROR_DUPLICATE_OPTION) {
        this.$set(this.optionErrors, index, '')
      }
    })

    const valueMap = new Map<string, number>()

    this.dropdownOptions.forEach((option, index) => {
      const trimmed = option.trim()
      if (!trimmed) return

      if (valueMap.has(trimmed)) {
        const firstIndex = valueMap.get(trimmed)!
        this.$set(this.optionErrors, firstIndex, ERROR_DUPLICATE_OPTION)
        this.$set(this.optionErrors, index, ERROR_DUPLICATE_OPTION)
      } else {
        valueMap.set(trimmed, index)
      }
    })
  }

  validateAllDropdownOptions(): void {
    this.dropdownOptions.forEach((_, index) => {
      this.validateDropdownOption(index)
    })
    this.validateUniqueness()
  }

  validateDropdownOptions(): boolean {
    this.dropdownOptionsError = ''

    const validOptions = this.getTrimmedDropdownOptions()
    if (validOptions.length < MIN_DROPDOWN_OPTIONS) {
      this.dropdownOptionsError = ERROR_MIN_OPTIONS
      return false
    }

    let allValid = true
    this.dropdownOptions.forEach((_, index) => {
      if (!this.validateDropdownOption(index)) {
        allValid = false
      }
    })

    const uniqueValues = new Set(validOptions)
    if (uniqueValues.size !== validOptions.length) {
      this.validateUniqueness()
      allValid = false
    }

    return allValid
  }

  onDefaultValueSelect(value: string) {
    this.defaultValue = value
    this.defaultValueError = ''
  }

  async handleSubmit() {
    const variableNameToUse = this.effectiveVariableName.trim()
    const sanitizedDisplayLabel = sanitizeDisplayLabel(this.displayLabel)
    const displayLabelToUse = sanitizedDisplayLabel || variableNameToUse

    if (!variableNameToUse) {
      this.variableNameError = this.$t('variableModal.errors.nameRequired') as string
      return
    }

    if (!this.selectedType) {
      this.typeError = this.$t('variableModal.errors.typeRequired') as string
      return
    }

    if (this.isDateLikeType && !this.selectedDateFormat) {
      this.dateFormatError = this.$t('variableModal.errors.dateFormatRequired') as string
      return
    }

    if (this.isDropdownType) {
      if (this.dropdownOptions.some(value => !value || !value.trim())) {
        this.dropdownOptionsError = ERROR_EMPTY_OPTIONS
        this.loading = false
        return
      }

      const validOptions = this.getTrimmedDropdownOptions()

      if (validOptions.length < MIN_DROPDOWN_OPTIONS) {
        this.dropdownOptionsError = ERROR_MIN_OPTIONS
        this.loading = false
        return
      }

      const uniqueValues = new Set<string>()
      for (const value of validOptions) {
        if (value.length < MIN_OPTION_LENGTH || value.length > MAX_OPTION_LENGTH) {
          this.dropdownOptionsError = ERROR_OPTION_LENGTH
          this.loading = false
          return
        }
        if (!DROPDOWN_OPTION_REGEX.test(value)) {
          this.dropdownOptionsError = ERROR_INVALID_CHARACTERS
          this.loading = false
          return
        }
        if (uniqueValues.has(value)) {
          this.dropdownOptionsError = ERROR_DUPLICATE_OPTIONS
          this.loading = false
          return
        }
        uniqueValues.add(value)
      }
    }

    if (!this.validateDefaultValue()) {
      return
    }

    const scopeId = this.scopeId || getCurrentSpaceId(this.$route)
    if (!scopeId) {
      this.snackbarStore.enqueue({
        variant: 'alert',
        leadingIcon: true,
        label: this.$t('variableModal.messages.error') as string
      })
      return
    }

    if (this.isEditMode && !this.variable?.id) {
      this.snackbarStore.enqueue({
        variant: 'alert',
        leadingIcon: true,
        label: this.$t('variableModal.messages.error') as string
      })
      return
    }

    this.variableNameError = ''
    this.typeError = ''
    this.defaultValueError = ''
    this.dateFormatError = ''

    const spaceStore = useSpaceStore()
    const isTemplateScope = !!this.scopeId && this.scopeId !== spaceStore.currentSpaceId
    if (!isTemplateScope) {
      if (this.suppressedForUnlock) {
        return
      }
      if (promptUnlockIfVariableSchemaLocked(this.variableSchemaUnlockOptions())) {
        this.suppressedForUnlock = true
        return
      }
    }

    this.loading = true

    const buildRequest = () => {
      const request: any = {
        variableName: variableNameToUse,
        displayLabel: displayLabelToUse,
        variableType: mapTypeToApi(this.selectedType),
        defaultValue: (this.defaultValue || '').trim() || undefined,
        dateFormat: this.isDateLikeType ? this.selectedDateFormat?.value : undefined
      }
      if (this.isDropdownType) {
        const validOptions = this.getTrimmedDropdownOptions()
        request.options = validOptions.map((value, index) => ({
          value,
          order: index + 1
        }))
      }
      return request
    }

    try {
      if (this.isEditMode && this.variable) {
        const request = buildRequest()
        const updatedVariable = isTemplateScope
          ? await this.spaceVariableStore.updateTemplateVariable(this.scopeId!, this.variable.id, request)
          : await this.spaceVariableStore.updateVariable(scopeId, this.variable.id, request)

        await this.$nextTick()

        if (isTemplateScope) {
          this.updateVariablePlaceholdersInAllEditors(updatedVariable.id, updatedVariable.variableName)
        }

        this.snackbarStore.enqueue({
          variant: 'success',
          leadingIcon: true,
          label: this.$t('variableModal.messages.successModify') as string
        })

        this.$emit('submit', updatedVariable)
      } else {
        const request = new CreateSpaceVariableRequest()
        Object.assign(request, buildRequest())

        const createdVariable = isTemplateScope
          ? await this.spaceVariableStore.createTemplateVariable(scopeId, request)
          : await this.spaceVariableStore.createVariable(scopeId, request)

        this.snackbarStore.enqueue({
          variant: 'success',
          leadingIcon: true,
          label: this.$t('variableModal.messages.successCreate') as string
        })

        this.$emit('submit', createdVariable)
      }
    } catch (error) {
      if (
        !this.suppressedForUnlock &&
        handleDocumentSignedVariableSchemaError(error, !isTemplateScope, this.variableSchemaUnlockOptions())
      ) {
        this.suppressedForUnlock = true
        return
      }
      this.snackbarStore.enqueue({
        variant: 'alert',
        leadingIcon: true,
        label: this.$t('variableModal.messages.error') as string
      })
    } finally {
      this.loading = false
    }
  }

  handleClose() {
    if (this.suppressedForUnlock) {
      cancelVariableSchemaUnlockFlow()
    }
    this.variableNameData = ''
    this.displayLabel = ''
    this.selectedType = 'Text'
    this.defaultValue = ''
    this.selectedDateFormat = null
    this.dropdownOptions = []
    this.optionErrors = []
    this.dropdownOptionsError = ''
    this.variableNameError = ''
    this.typeError = ''
    this.defaultValueError = ''
    this.dateFormatError = ''
    this.loading = false
    this.suppressedForUnlock = false
    this.$emit('close')
  }

  updateVariablePlaceholdersInAllEditors(variableId: string, variableName: string): void {
    const allComponents: any[] = []

    const findComponents = (component: VueConstructor): void => {
      if (component && typeof (component as any).updateVariablePlaceholdersAfterAssignment === 'function') {
        // Static blocks too: the method updates a live editor, or a static block's stored HTML
        allComponents.push(component)
      }
      if (component.$children?.length) {
        component.$children.forEach(child => findComponents(child as VueConstructor))
      }
    }

    let root = this.$root
    while (root.$parent) {
      root = root.$parent
    }
    findComponents(root)

    for (const component of allComponents) {
      component.updateVariablePlaceholdersAfterAssignment(variableId, variableName)
    }
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles' as *;

.form-fields {
  display: flex;
  flex-direction: column;
  gap: $notch-space-4x;
}

.date-picker-field {
  display: flex;
  flex-direction: column;
  gap: $notch-space-1x;
}

.date-picker-label {
  @include notch-text-label-light-XS;
  display: block;
  color: $notch-color-neutral-sand-600;
  padding: 0 $notch-space-4x;
}

.date-picker-field :deep(.variable-date-datepicker .variable-date-input-wrapper) {
  position: relative;
  box-sizing: border-box;
  outline: 2px solid $notch-color-neutral-sand-200;
  @include notch-style-focus-outline-base;
  border-radius: $notch-radius-10x;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: $notch-color-neutral-grey-000;
}

.date-picker-field :deep(.variable-date-datepicker .variable-date-input-wrapper:hover) {
  outline-color: $notch-color-neutral-sand-300;
}

.date-picker-field :deep(.variable-date-datepicker .variable-date-input-wrapper:focus-within) {
  @include notch-style-focus-outline;
  outline-color: $notch-color-neutral-sand-300;
}

.date-picker-field :deep(.variable-date-datepicker .variable-date-input) {
  @include notch-text-label-light-M;
  display: inline-block;
  border: none;
  padding: 18px $notch-space-4x;
  margin: 0;
  outline: none;
  width: 100%;
  height: $notch-space-8_5x;
  box-sizing: border-box;
  background-color: transparent;
  color: $notch-color-neutral-grey-900;
  box-shadow: none;

  &::placeholder {
    color: $notch-color-neutral-sand-500;
  }
}

.field-error {
  @include notch-text-label-light-XS;
  display: block;
  margin-top: $notch-space-1x;
  color: $notch-color-semantic-alert-500;
  padding: 0 $notch-space-4x;
}

.dropdown-options-field {
  display: flex;
  flex-direction: column;
  gap: $notch-space-2x;
}

.dropdown-options-label {
  @include notch-text-label-light-XS;
  display: block;
  color: $notch-color-neutral-sand-600;
  padding: 0 $notch-space-4x;
}

.dropdown-options-scroll-container {
  max-height: 205px;
  overflow-y: auto;
  padding-right: $notch-space-2x;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 4px;
  }

  &.show-scroll {
    scrollbar-color: $notch-color-neutral-sand-300 transparent;

    &::-webkit-scrollbar-thumb {
      background: $notch-color-neutral-sand-300;

      &:hover {
        background: $notch-color-neutral-sand-400;
      }
    }
  }
}

.dropdown-options-list {
  display: flex;
  flex-direction: column;
  gap: $notch-space-2x;
}

.dropdown-option-row {
  display: flex;
  align-items: flex-start;
  gap: $notch-space-2x;

  .dropdown-option-input {
    flex: 0 0 80%;
    min-width: 0;
  }
}

.dropdown-option-actions {
  display: flex;
  align-items: center;
  gap: $notch-space-1x;
  padding-top: $notch-space-2x;
  flex-shrink: 0;
  min-width: fit-content;

  .delete-icon,
  .add-icon {
    cursor: pointer;
    color: $notch-color-neutral-sand-600;
    flex-shrink: 0;

    &:hover {
      color: $notch-color-neutral-grey-900;
    }
  }
}
</style>
